import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const FRESH_MS = 5 * 60 * 1000;

// The whole published range is a few hundred products, so the shop loads it
// once and filters/sorts/counts in the browser. Kept for a few minutes so
// moving between the landing page and the catalogue doesn't refetch.
let cache = null; // { at, products, maintenance }
let inflight = null;

async function load() {
  const [productsRes, settingsRes] = await Promise.all([
    fetch(`${API}/api/products?limit=1000`),
    fetch(`${API}/api/settings/features/public`).catch(() => null),
  ]);
  if (!productsRes.ok) throw new Error("Failed to load products");
  const products = await productsRes.json();
  let maintenance = false;
  if (settingsRes && settingsRes.ok) {
    const settings = await settingsRes.json().catch(() => ({}));
    maintenance = settings.store_maintenance_mode === true;
  }
  return { at: Date.now(), products: Array.isArray(products) ? products : [], maintenance };
}

/** { products, loading, error, maintenance } for the public shop pages. */
export default function useStoreProducts() {
  const fresh = cache && Date.now() - cache.at < FRESH_MS ? cache : null;
  const [state, setState] = useState(() =>
    fresh
      ? { products: fresh.products, maintenance: fresh.maintenance, loading: false, error: "" }
      : { products: [], maintenance: false, loading: true, error: "" }
  );

  useEffect(() => {
    if (fresh) return undefined;
    let alive = true;
    inflight = inflight || load().finally(() => (inflight = null));
    inflight
      .then((data) => {
        cache = data;
        if (alive) setState({ products: data.products, maintenance: data.maintenance, loading: false, error: "" });
      })
      .catch(() => {
        if (alive) setState((s) => ({ ...s, loading: false, error: "We couldn't load the store just now. Please try again." }));
      });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}
